import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Skeleton
} from "@livingdesign/react";
import { Plus } from "@livingdesign/icons";
import { onInputNumberKeyDown, sortByString } from "../../../helpers/utils";
import { amountFormat } from "../../../helpers/commonUtils";
import { AddBudgetByUTBMSStyles } from "./AddBudgetByUTBMS.styles";
import MuiDataTable from "../../../components/Table/MuiDataTable/MuiDataTable";
import MuiValidationMessage from "../../../Validation/MuiValidationMessage/MuiValidationMessage";
import UtbmsActivityAutocomplete from "../../../components/Autocomplete/UtbmsActivityAutocomplete/UtbmsActivityAutocomplete";

const AddBudgetByUTBMS = ({
  loading,
  data,
  dataLoading,
  masterDetails,
  mode,
  onUtbmsDataChange,
  initialData = []
}) => {
  const [utbmsEntries, setUtbmsEntries] = useState(initialData.length > 0 ? initialData : [
    { id: 1, code: null, displayName: "", numberOfHours: "", budgetAmount: "", new: true }
  ]);
  const [utbmsItemsError, setUtbmsItemsError] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const classes = AddBudgetByUTBMSStyles();
  let idNum = utbmsEntries.length;

  // Initialize UTBMS entries with existing data or default empty entry
  useEffect(() => {
    if (data?.budgetTasks && data.budgetTasks.length > 0 && utbmsEntries.length > 0) {
      // Edit mode: populate with existing UTBMS/task data
      const existingTasks = data.budgetTasks.map((task, index) => ({
        id: index,
        code: utbmsEntries?.find(x => x.value === task.code)?.code || null,
        value: task.code || null,
        displayName: task.displayName || task.description || "",
        numberOfHours: task.numberOfHours || "",
        budgetAmount: task.budgetAmount || "",
        new: false
      }));
      const totalHrs = existingTasks.reduce((sum, entry) => {
        return sum + (parseFloat(entry.numberOfHours) || 0);
      }, 0);

      const totalAmt = existingTasks.reduce((sum, entry) => {
        return sum + (parseFloat(entry.budgetAmount) || 0);
      }, 0);

      setTotalHours(totalHrs);
      setTotalAmount(totalAmt);
      setSelectedValues(existingTasks);
    } else if (initialData.length > 0) {
      // Use initial data if provided
      setSelectedValues(initialData);
    } else {
      // Create mode: initialize with one empty entry
      setSelectedValues([
        { id: 1, code: null, value: null, displayName: "", numberOfHours: "", budgetAmount: "", new: true }
      ]);
    }
  }, [JSON.stringify(data?.budgetTasks), JSON.stringify(initialData), utbmsEntries.length]);

  const COLUMNS = [
    {
      name: "code",
      label: "UTBMS Code*",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            minWidth: '250px',
            width: '250px',
            maxWidth: '250px',
            fontWeight: 'bold',
            color: 'black'
          }
        }),
        setCellProps: () => ({
          style: {
            minWidth: '250px',
            width: '250px',
            maxWidth: '250px',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }
        }),
        customBodyRender: (value, tableMeta) => {
          const rowData = selectedValues[tableMeta.rowIndex];
          if (!rowData || rowData.isTotal) {
            return <strong>{rowData?.isTotal ? 'Total' : value}</strong>;
          }

          return (
            <UtbmsActivityAutocomplete
              name="code"
              value={value ? JSON.stringify(utbmsEntries?.filter((item) => item.code == value)?.[0]) : null}
              tableMeta={tableMeta}
              setValue={(value) => setUtbmsCode(value, rowData.id)}
              data={masterDetails ? utbmsEntries : []}
              placeholder="Select UTBMS Code"
              showDescription={true}
              required={true}
              testid={`utbmsCode-${rowData.id}`}
              disablePortal={false}
              fromBudget={true}
              handleBlur={() => { }}
              selectedValues={selectedValues}
              mode={mode}
            />
          )

        }
      }
    },
    {
      name: "description",
      label: "Description",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            fontWeight: 'bold',
            color: 'black'
          }
        }),
        customBodyRender: (value, tableMeta) => {
          const rowData = selectedValues[tableMeta.rowIndex];
          if (!rowData || rowData.isTotal) {
            return <span>{"-"}</span>;
          }

          return (
            <span>{rowData?.displayName ?? "-"}</span>
          )
        }
      }
    },
    {
      name: "numberOfHours",
      label: "# Hours",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            fontWeight: 'bold',
            color: 'black'
          }
        }),
        setCellProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px'
          }
        }),
        customBodyRender: (value, tableMeta) => {
          const rowData = selectedValues[tableMeta.rowIndex];
          if (!rowData || rowData.isTotal) {
            return <strong>{value}</strong>;
          }

          const showHoursError = utbmsItemsError?.find(x => x.name === 'numberOfHours' && x.rowIndex === rowData.id);

          return  (
            <TextField
              value={rowData.numberOfHours}
              placeholder="Hours"
              data-testid="budget_utbms_hours"
              textFieldProps={{
                id: "numberOfHours",
                name: "numberOfHours",
                min: "0",
                step: "any"
              }}
              error={showHoursError?.error && <MuiValidationMessage message={'Required'} mode="LD" />}
              onChange={(e) => handleInputChangeUtbmsEntry(e.target.name, e.target.value, rowData.id)}
              onKeyDown={(e) => onInputNumberKeyDown(e)}
              onWheel={(e) => { e.target.blur() }}
              type="number"
              pattern="[0,9]*"
            />
          )
        }
      }
    },
    {
      name: "budgetAmount",
      label: "$ Budget Amount*",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px',
            fontWeight: 'bold',
            color: 'black'
          }
        }),
        setCellProps: () => ({
          style: {
            minWidth: '150px',
            width: '150px',
            maxWidth: '150px'
          }
        }),
        customBodyRender: (value, tableMeta) => {
          const rowData = selectedValues[tableMeta.rowIndex];
          if (!rowData || rowData.isTotal) {
            return <strong>{amountFormat(value)}</strong>;
          }

          const showBudgetError = utbmsItemsError?.find(x => x.name === 'budgetAmount' && x.rowIndex === rowData.id);

          return (
            <TextField
              value={rowData.budgetAmount}
              placeholder="Amount"
              data-testid="budget_utbms_budgetAmount"
              textFieldProps={{
                id: "budgetAmountNew",
                name: "budgetAmount",
                min: "0",
                step: "any"
              }}
              error={showBudgetError?.error && <MuiValidationMessage message={'Required'} mode="LD" />}
              onChange={(e) => handleInputChangeUtbmsEntry(e.target.name, e.target.value, rowData.id)}
              onKeyDown={(e) => onInputNumberKeyDown(e)}
              onWheel={(e) => { e.target.blur() }}
              type="number"
              pattern="[0,9]*"
            />
          )
        }
      }
    },
    {
      name: "action",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            paddingLeft: '32px',
            fontWeight: 'bold',
            color: 'black'
          }
        }),
        customBodyRender: (value, tableMeta) => {
          const rowData = selectedValues[tableMeta.rowIndex];
          if (!rowData || rowData.isTotal) {
            return null;
          }

          return (
            <Button
              id="remove-utbms"
              variant="tertiary"
              onClick={() => handleRemoveUtbmsEntry(rowData?.id)}
              data-testid={`budget_utbms_remove`}
            >
              {"Remove"}
            </Button>
          );
        }
      }
    }
  ]

  // Notify parent component of data changes
  useEffect(() => {
    // Calculate totals
    const totalHours = selectedValues.reduce((sum, entry) => {
      const numberOfHours = parseFloat(entry.numberOfHours) || 0;
      return sum + numberOfHours;
    }, 0);
    setTotalHours(totalHours)

    const totalAmount = selectedValues.reduce((sum, entry) => {
      const amount = parseFloat(entry.budgetAmount) || 0;
      return sum + amount;
    }, 0);
    setTotalAmount(totalAmount)
    if (selectedValues.length > 0) {
      onUtbmsDataChange(selectedValues);
    }
  }, [selectedValues]);

  // Update utbmsEntries from masterDetails for autocomplete options
  useEffect(() => {
    if (masterDetails) {
      const expenseCode = masterDetails?.expenseCode?.masterData || [];
      const phaseCode = masterDetails?.phaseCode?.masterData || [];
      const taskCode = masterDetails?.taskCode?.masterData || [];
      const utbmsEntries = sortByString([...expenseCode, ...phaseCode, ...taskCode], "value");
      setUtbmsEntries(utbmsEntries);
    }
  }, [masterDetails]);

  const handleAddUtbmsEntry = () => {
    const maxId = selectedValues.length > 0 ? Math.max(...selectedValues.map(entry => entry.id)) : -1;
    const newEntry = {
      id: maxId + 1,
      code: null,
      value: null,
      displayName: "",
      numberOfHours: "",
      budgetAmount: "",
      new: true
    };
    setSelectedValues([...selectedValues, newEntry]);
  };

  const handleRemoveUtbmsEntry = (id) => {
    const updatedEntries = selectedValues.filter(entry => entry.id !== id);
    setSelectedValues(updatedEntries);
    onUtbmsDataChange(updatedEntries);
    // Remove any errors for this entry
    setUtbmsItemsError(prev => prev.filter(error => error.rowIndex !== id));
  };

  const setUtbmsCode = (value, id) => {
    const parsedValue = value ? JSON.parse(value) : null;
    const updatedEntries = selectedValues.map(entry => {
      if (entry.id === id) {
        return {
          ...entry,
          code: parsedValue?.code,
          value: parsedValue?.value,
          displayName: parsedValue?.description || ""
        };
      }
      return entry;
    }).filter(entry => entry.code);
    setSelectedValues(updatedEntries);
  };

  const handleInputChangeUtbmsEntry = (fieldName, value, id) => {
    const values = selectedValues.map(entry => {
      if (entry.id === id) {
        return {
          ...entry,
          [fieldName]: fieldName === 'budgetAmount' ? parseFloat(value) || 0 : value
        };
      }
      return entry;
    });
    setSelectedValues(values);

    // Clear any existing errors for this field
    setUtbmsItemsError(prev =>
      prev.filter(error => !(error.name === fieldName && error.rowIndex === id))
    );
  };


  const customOptions = {
    selectableRows: "none",
    pagination: false,
    search: false,
    filter: false,
    viewColumns: false,
    download: false,
    print: false,
    responsive: "standard",
    tableBodyHeight: "auto",
    elevation: 0
  }

  return (
    <div
      className={classes.budgetTypePhase}
      data-testid="budget-utbms"
    >
      {loading || dataLoading ? (
        <Skeleton height="58px" variant="rectangle" />
      ) : (
        <>
          <div className={classes.addPhase}>
            <div
              className={classes.cursorPointer}
              data-testid="budget_utbms_addEntry"
              onClick={handleAddUtbmsEntry}
            >
              <Plus size="medium" />
              <span className={classes.addNewText} id="add-new-utbms">Add UTBMS Code</span>
            </div>
          </div>

          <MuiDataTable
            data={[...selectedValues, {
              id: 'total',
              code: 'Total',
              value: 'Total',
              displayName: `${selectedValues.length} UTBMS Code(s)`,
              numberOfHours: amountFormat(totalHours),
              budgetAmount: `$ ${amountFormat(totalAmount)}`,
              isTotal: true
            }]}
            columns={COLUMNS}
            customOptions={customOptions}
            disableSearch={true}
            disableFilter={true}
            disablePagination={true}
            disableExport={true}
            customClassName={classes.utbmsTable}
            hideAddNewButton={true}
          />
        </>
      )}
    </div>
  );
};

export default AddBudgetByUTBMS;
